import React from "react";
import {
  ShieldCheck,
  TrendingDown,
  Truck,
  CreditCard,
  Headphones,
  FileCheck,
  Shield,
  Clock,
  Lightbulb,
  Leaf,
  Users,
  MapPin,
  Handshake,
} from "lucide-react";
import Card from "../ui/Card";

const Features = () => {
  // Core Values from Brochure
  const coreValues = [
    {
      id: 1,
      icon: Shield,
      title: "INTEGRITY",
      description: "Ethical sourcing and transparent pricing",
      color: "text-primary-700",
      bgColor: "bg-primary-50",
    },
    {
      id: 2,
      icon: Clock,
      title: "RELIABILITY",
      description: "On-time deliveries, every time",
      color: "text-primary-700",
      bgColor: "bg-primary-50",
    },
    {
      id: 3,
      icon: Lightbulb,
      title: "INNOVATION",
      description: "Continuous process and product improvements",
      color: "text-primary-700",
      bgColor: "bg-primary-50",
    },
    {
      id: 4,
      icon: Leaf,
      title: "SUSTAINABILITY",
      description: "Promoting eco-friendly materials and practices",
      color: "text-primary-700",
      bgColor: "bg-primary-50",
    },
    {
      id: 5,
      icon: Users,
      title: "CUSTOMER-CENTRICITY",
      description: "Solutions tailored to your project needs",
      color: "text-primary-700",
      bgColor: "bg-primary-50",
    },
  ];

  // Core Services from Brochure
  const services = [
    {
      id: 1,
      icon: ShieldCheck,
      title: "Quality Assurance",
      description:
        "Rigorous inspection and batch tracking ensure every material meets industry standards before dispatch.",
      color: "text-secondary-600",
      bgColor: "bg-secondary-50",
    },
    {
      id: 2,
      icon: TrendingDown,
      title: "Best Price",
      description:
        "Competitive sourcing and strong vendor relationships allow us to offer you the most cost-effective solutions.",
      color: "text-secondary-600",
      bgColor: "bg-secondary-50",
    },
    {
      id: 3,
      icon: Truck,
      title: "Logistics",
      description:
        "End-to-end coordination with trusted carriers guarantees smooth, on-time deliveries to any location.",
      color: "text-secondary-600",
      bgColor: "bg-secondary-50",
    },
    {
      id: 4,
      icon: FileCheck,
      title: "Paperwork",
      description:
        "Comprehensive handling of all documentation—purchase orders, statutory certificates, and compliance documents.",
      color: "text-secondary-600",
      bgColor: "bg-secondary-50",
    },
    {
      id: 5,
      icon: CreditCard,
      title: "Payment & Credit",
      description:
        "Flexible payment terms and credit facilities designed to ease your working-capital requirements.",
      color: "text-secondary-600",
      bgColor: "bg-secondary-50",
    },
    {
      id: 6,
      icon: MapPin,
      title: "Pan-India Delivery",
      description:
        "A robust distribution network ensures access to materials across the country, from metros to remote sites.",
      color: "text-secondary-600",
      bgColor: "bg-secondary-50",
    },
    {
      id: 7,
      icon: Handshake,
      title: "Reliable Partnership",
      description:
        "Dedicated support and transparent communication at every step, making us more than a supplier.",
      color: "text-secondary-600",
      bgColor: "bg-secondary-50",
    },
  ];

  return (
    <>
      {/* Core Values Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              CORE VALUES
            </h2>
            <p className="text-lg text-gray-600">
              The principles that guide everything we do at lohamart India
            </p>
          </div>

          {/* Values Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {coreValues.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card
                  key={value.id}
                  hover
                  className="group animate-slide-up text-center bg-white border-2 border-gray-100 hover:border-secondary-400"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="space-y-4">
                    <div
                      className={`w-16 h-16 ${value.bgColor} rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300`}
                    >
                      <Icon className={`w-8 h-8 ${value.color}`} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 tracking-wide">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm">
                      {value.description}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gray-900">OUR CORE </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary-500 to-secondary-600">
                SERVICES
              </span>
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive solutions for all your construction material needs
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card
                  key={service.id}
                  hover
                  className="group animate-slide-up bg-gradient-to-br from-white to-gray-50 border border-gray-200 hover:border-secondary-400 hover:shadow-xl transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="space-y-4">
                    <div
                      className={`w-14 h-14 ${service.bgColor} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
                    >
                      <Icon className={`w-7 h-7 ${service.color}`} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm">
                      {service.description}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default Features;
